import * as chrono from 'chrono-node'

export interface TimeExpressionMatch {
  text: string
  startIndex: number
  endIndex: number
  date: Date
}

/** Pattern matching day abbreviations chrono-node doesn't recognize */
const DAY_ALIAS_PATTERN = /\b(tues|weds)\b/gi

/**
 * Normalize day abbreviations that chrono-node doesn't recognize.
 * Removes the trailing 's' from 'tues' → 'tue' and 'weds' → 'wed'.
 * Returns positions of removed characters for index mapping.
 */
function normalizeDayAbbreviations(input: string): { normalized: string; removed: Array<number> } {
  const removed: Array<number> = []
  const normalized = input.replace(DAY_ALIAS_PATTERN, (match, _group, offset) => {
    removed.push(offset + 3)
    return match.slice(0, 3)
  })
  return { normalized, removed }
}

/** Map an index in the normalized string back to the original string */
function toOriginalIndex(normalizedIdx: number, removed: Array<number>): number {
  let offset = 0
  for (const pos of removed) {
    if (normalizedIdx >= pos - offset) {
      offset++
    } else {
      break
    }
  }
  return normalizedIdx + offset
}

export function parseTimeExpression(input: string, now?: Date): TimeExpressionMatch | null {
  const referenceDate = now ?? new Date()
  const { normalized, removed } = normalizeDayAbbreviations(input)
  const results = chrono.parse(normalized, referenceDate, { forwardDate: true })

  if (results.length === 0) return null

  const result = results[0]
  const startIndex = toOriginalIndex(result.index, removed)
  const endIndex = toOriginalIndex(result.index + result.text.length, removed)

  return {
    text: input.slice(startIndex, endIndex),
    startIndex,
    endIndex,
    date: result.start.date(),
  }
}
